import { useMemo, useState } from 'react';
import { FormattedMessage, FormattedPlural, useIntl } from 'react-intl';
import size from 'lodash/fp/size';
import isEmpty from 'lodash/fp/isEmpty';
import TableToolbar from '@rio-cloud/rio-uikit/TableToolbar';
import TableSearch from '@rio-cloud/rio-uikit/TableSearch';
import Button from '@rio-cloud/rio-uikit/Button';

import { getPartnerList, getShowCreateDialog, showInvitationDialog } from '../../layout/appSlice';
import { getTrackingAttributes, TRACKING_CATEGORIES } from '../../configuration/setup/googleAnalytics';
import { useAppDispatch, useAppSelector } from '../../configuration/setup/hooks';
import ConnectionTypeFilter from './ConnectionTypeFilter';
import CreatePartnerDialog from '../createPartnerDialog/CreatePartnerDialog';
import HelpButton from '../helpButton/HelpButton';
import PartnerTable from './PartnerTable';
import type { ConnectionType } from '../../services/partnerNetwork.types';

const Partners = () => {
    const [searchValue, setSearchValue] = useState<string>('');
    const [connectionTypes, setConnectionTypes] = useState<ConnectionType[]>([]);

    const intl = useIntl();
    const dispatch = useAppDispatch();

    const showCreatePartnerDialog = useAppSelector(getShowCreateDialog);
    const partners = useAppSelector(getPartnerList)?.items || [];

    const handleCreatePartner = () => dispatch(showInvitationDialog({ showDialog: true }));
    const handleCloseCreatePartnerDialog = () => dispatch(showInvitationDialog({ showDialog: false }));

    const filteredPartners = useMemo(
        () =>
            partners.filter(partner => {
                const matchesSearch =
                    isEmpty(searchValue) ||
                    partner?.name?.includes(searchValue) ||
                    partner?.description?.includes(searchValue) ||
                    partner.partnerConnectionDisplayName?.includes(searchValue);
                const matchesConnection = isEmpty(connectionTypes) || connectionTypes.includes(partner.connectionType);
                return matchesSearch && matchesConnection;
            }),
        [partners, searchValue, connectionTypes]
    );

    const totalPartners = size(filteredPartners);

    const isEmptyList = isEmpty(filteredPartners) && isEmpty(searchValue);
    const isNotFoundList = isEmpty(filteredPartners) && !isEmpty(searchValue);

    return (
        <div className='container fluid-large'>
            <CreatePartnerDialog show={showCreatePartnerDialog} onClose={handleCloseCreatePartnerDialog} />
            <TableToolbar>
                <div className='table-toolbar-container'>
                    <div className='table-toolbar-group-left'>
                        <div className='table-toolbar-column'>
                            <div className='btn-toolbar table-btn-toolbar'>
                                <Button
                                    bsStyle='primary'
                                    onClick={handleCreatePartner}
                                    {...getTrackingAttributes({
                                        trigger: 'click',
                                        category: TRACKING_CATEGORIES.OVERVIEW,
                                        action: 'Add partner clicked',
                                        label: 'Add partner clicked',
                                    })}
                                >
                                    <span className='rioglyph rioglyph-plus' />
                                    <FormattedMessage id='intl-msg:network.label.addPartner' />
                                </Button>
                                <HelpButton />
                            </div>
                        </div>
                    </div>
                    <div className='table-toolbar-group-right'>
                        <div className='table-toolbar-column justify-content-center'>
                            <div className='label label-muted label-filled'>
                                <FormattedPlural
                                    one={
                                        <FormattedMessage
                                            id='intl-msg:network.partnersTable.numPartners.one'
                                            values={{ count: totalPartners }}
                                        />
                                    }
                                    other={
                                        <FormattedMessage
                                            id='intl-msg:network.partnersTable.numPartners.other'
                                            values={{ count: totalPartners }}
                                        />
                                    }
                                    zero={<FormattedMessage id='intl-msg:network.partnersTable.numPartners.zero' />}
                                    value={totalPartners}
                                />
                            </div>
                        </div>
                        <div className='table-toolbar-column justify-content-center'>
                            <ConnectionTypeFilter onConnectionTypeChange={setConnectionTypes} />
                        </div>
                        <div className='table-toolbar-column'>
                            <TableSearch
                                value={searchValue}
                                onChange={setSearchValue}
                                placeholder={intl.formatMessage({
                                    id: 'intl-msg:network.partnersTable.search.placeholder',
                                })}
                            />
                        </div>
                    </div>
                </div>
            </TableToolbar>
            <PartnerTable partners={filteredPartners} isEmptyList={isEmptyList} isNotFoundList={isNotFoundList} />
        </div>
    );
};

export default Partners;
